/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/ProfReportsLayout.tsx
   Lateral con los 4 informes disponibles:
     • Desglose horas trabajadas
     • Horas semanales
     • Alumnos por profesor
     • Incidencias
   ────────────────────────────────────────────────────────────── */

import { Outlet, NavLink } from 'react-router-dom';
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import ScheduleIcon from '@mui/icons-material/Schedule';
import NumbersIcon from '@mui/icons-material/Numbers';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useTranslation } from 'react-i18next';
/* helper para estilizar el item activo */
const linkSx = (isActive: boolean) => ({
  bgcolor: isActive ? 'action.selected' : 'transparent',
  color: isActive ? 'primary.main' : 'text.primary',
  '& .MuiListItemIcon-root': {
    color: isActive ? 'primary.main' : 'inherit',
    minWidth: 32,
  },
});

export default function ProfReportsLayout() {
  const { t } = useTranslation<'translation'>();
  return (
    <Box sx={{ display: 'flex', minHeight: '100%' }}>
      {/* ─────────── lateral: sub-menú ─────────── */}
      <Box
        sx={{
          width: 220,
          borderRight: 1,
          borderColor: 'divider',
          px: 2,
          py: 3,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t('profReportsLayout.titulo')}
        </Typography>

        <List dense disablePadding>
          {/* Desglose mensual */}
          <NavLink to="horas">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <BarChartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.desglose')} />
              </ListItemButton>
            )}
          </NavLink>

          {/* Horas semanales */}
          <NavLink to="semanal">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <BarChartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.semanal')} />
              </ListItemButton>
            )}
          </NavLink>

          {/* Horas semanales contratadas */}
          <NavLink to="horas-contratadas">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <NumbersIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.horasContratadas')} />
              </ListItemButton>
            )}
          </NavLink>

          {/* Alumnos por profesor */}
          <NavLink to="alumnos">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <PeopleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.alumnos')} />
              </ListItemButton>
            )}
          </NavLink>
          <NavLink to="asignaturas">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <MenuBookIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.asignaturas')} />
              </ListItemButton>
            )}
          </NavLink>

          {/* Incidencias */}
          <NavLink to="incidencias">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <ReportProblemIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.incidencias')} />
              </ListItemButton>
            )}
          </NavLink>
          {/* Retrasos */}
          <NavLink to="retrasos">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <ScheduleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.retrasos')} />
              </ListItemButton>
            )}
          </NavLink>
          <NavLink to="one-minute-paper-incumplimiento">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <PersonOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t(
                    'profReportsLayout.menu.oneMinutePaperIncumplimiento',
                    'Incumplimiento ejercicio de inicio de clase',
                  )}
                />
              </ListItemButton>
            )}
          </NavLink>
          <NavLink to="total">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <NumbersIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.total')} />
              </ListItemButton>
            )}
          </NavLink>
          <NavLink to="A365">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <SchoolIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.a365')} />
              </ListItemButton>
            )}
          </NavLink>
          <NavLink to="high-perfomance">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <SchoolIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.highperformance')} />
              </ListItemButton>
            )}
          </NavLink>
          <NavLink to="ocupacion">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <BarChartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.ocupacion')} />
              </ListItemButton>
            )}
          </NavLink>
          <NavLink to="horarios">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <ScheduleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.heatmapHorarios')} />
              </ListItemButton>
            )}
          </NavLink>
          <NavLink to="formaciones">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <SchoolIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.formaciones')} />
              </ListItemButton>
            )}
          </NavLink>

          <NavLink to="prevision">
            {({ isActive }) => (
              <ListItemButton selected={isActive} sx={linkSx(isActive)}>
                <ListItemIcon>
                  <TrendingUpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('profReportsLayout.menu.prevision')} />
              </ListItemButton>
            )}
          </NavLink>
        </List>
      </Box>

      {/* ─────────── contenido del informe ─────────── */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
